import React, { useState, useEffect } from 'react';
import { taskAPI, userAPI } from '../services/api';
import './TaskCreate.css';

const TaskCreateForm = ({ onTaskCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState('assigned'); // Default to 'assigned'
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [assignees, setAssignees] = useState([]); // Changed to multiple assignees
  const [taskType, setTaskType] = useState('');
  const [client, setClient] = useState('');

  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New state for success and error messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [clientSearch, setClientSearch] = useState('');
  const [taskTypeSearch, setTaskTypeSearch] = useState('');
  const [prioritySearch, setPrioritySearch] = useState('');
  const [statusSearch, setStatusSearch] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');

// Add these dropdown visibility states
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showTaskTypeDropdown, setShowTaskTypeDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);


  const clientOptions = [
  'aakruti', 'amidhara', 'angel', 'babubhai jewellers', 'bela', 'benbaa', 'eighteen',
  'magmino', 'miral', 'neeta', 'other', 'rashi',
  'shakti industries', 'shalibhadra rental', 'shalibhadra ahm', 'sivi',
  'sliccily', 'stillman', 'surprise', 'sukanya', 'suryam', 'tantukk'
];

const taskTypeOptions = [
  'graphic', 'reel', 'post', 'caption #', 'schedule', 'find references',
  'check data', 'story', 'shoot', 'strategy', 'reel+cap', 'cap+post', 'other'
];

const priorityOptions = ['low', 'medium', 'high'];

const statusOptions = [
  'assigned', 'not started', 'in progress', 'team review', 'client review',
  'rejected by team', 'rejected by client', 'approved', 'completed', 'posted'
];

// Add filter functions
const filterOptions = (options, searchTerm) => {
  return options.filter(option => 
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );
};

const filterUsers = (users, searchTerm) => {
  return users.filter(user => {
    const name = user.username || user.name || user.email || `User ${user.id}`;
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });
};

// Add click outside handler
useEffect(() => {
  const handleClickOutside = (event) => {
    if (!event.target.closest('.custom-select')) {
      setShowClientDropdown(false);
      setShowTaskTypeDropdown(false);
      setShowPriorityDropdown(false);
      setShowStatusDropdown(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);


  // Get current date and time for minimum date/time validation
  const getCurrentDateTime = () => {
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const currentTime = now.toTimeString().slice(0, 5); // Format: HH:MM
    return { currentDate, currentTime };
  };

  const { currentDate, currentTime } = getCurrentDateTime();

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Try multiple endpoints to fetch users
        let usersData = [];
        
        try {
          // First try the users endpoint
          const usersRes = await userAPI.getUsers();
          usersData = usersRes.data;
        } catch (err) {
          console.log("Failed to fetch from users/, trying alternative endpoint...");
          
          try {
            // Try alternative endpoint that might be accessible to all users
            const usersRes = await userAPI.getAssignees();
            usersData = usersRes.data;
          } catch (err2) {
            console.log("Trying to fetch assignable users...");
            try {
              // If both fail, try getting assignable users
              const usersRes = await userAPI.getAssignableUsers();
              usersData = usersRes.data;
            } catch (err3) {
              // Last attempt - try to get all users with different endpoint
              console.log("Trying to fetch all team members...");
              const usersRes = await userAPI.getTeamMembers();
              usersData = usersRes.data;
            }
          }
        }
        
        setAllUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load assignee list. You may still create tasks but won't be able to assign them.");
        
        // Fallback: try to get current user at least
        try {
          const currentUserRes = await userAPI.getCurrentUser();
          setAllUsers([currentUserRes.data]);
        } catch (fallbackErr) {
          console.error("Failed to get current user:", fallbackErr);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle multiple assignee selection
  const handleAssigneeToggle = (userId) => {
    setAssignees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Remove assignee from selection
  const removeAssignee = (userId) => {
    setAssignees(prev => prev.filter(id => id !== userId));
  };

  // Get selected assignee names for display
  const getSelectedAssigneeNames = () => {
    return assignees.map(id => {
      const user = allUsers.find(u => u.id === id);
      return user ? (user.username || user.name || user.email || `User ${user.id}`) : '';
    });
  };

  // Validate if selected date/time is in the future
  const isValidDateTime = (date, time) => {
    if (!date) return false; // Date is now required
    
    const selectedDate = date; // This is already in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0]; // Get today in YYYY-MM-DD format

    if (selectedDate === today && time) {
    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // Format: HH:MM
    return time > currentTime;
  }
    
    return selectedDate >= today;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(''); // Clear previous error messages
    setSuccessMessage(''); // Clear previous success messages
    
    // Validate required fields
    if (!title.trim()) {
      setErrorMessage('Title is required');
      setIsSubmitting(false);
      return;
    }
    
    if (!description.trim()) {
      setErrorMessage('Description is required');
      setIsSubmitting(false);
      return;
    }
    
    if (!priority) {
      setErrorMessage('Priority is required');
      setIsSubmitting(false);
      return;
    }
    
    if (!dueDate) {
      setErrorMessage('Due date is required');
      setIsSubmitting(false);
      return;
    }
    
    if (assignees.length === 0) {
      setErrorMessage('At least one assignee is required');
      setIsSubmitting(false);
      return;
    }
    
    if (!taskType) {
      setErrorMessage('Task type is required');
      setIsSubmitting(false);
      return;
    }
    
    if (!client) {
      setErrorMessage('Client is required');
      setIsSubmitting(false);
      return;
    }
    
    // Validate date
    if (!isValidDateTime(dueDate, dueTime)) {
      if (dueDate === getCurrentDateTime().currentDate && dueTime) {
        setErrorMessage('Due time cannot be in the past for today\'s date');
     } else {
       setErrorMessage('Due date cannot be in the past');
     }
     setIsSubmitting(false);
     return;
    }

    // Prepare the data object - all fields are now required except status
    const data = {
      title: title.trim(),
      description: description.trim(),
      priority: priority,
      status: status || 'assigned', // Default to 'assigned' if empty
      due_date: dueDate,
      due_time: dueTime || null,
      assignees_ids: assignees,
      task_type: taskType,
      client: client
    };

    console.log('Submitting task data:', data);

    try {
      const response = await taskAPI.createTask(data);
      
      console.log('Task created successfully:', response.data);
      setSuccessMessage('Task created successfully! 🎉');
      onTaskCreated();

      // Reset form fields
      setTitle('');
      setDescription('');
      setPriority('');
      setStatus('assigned'); // Reset to default 'assigned'
      setDueDate('');
      setDueTime('');
      setAssignees([]); // Reset assignees array
      setTaskType('');
      setClient('');
    } catch (err) {
      console.error("Error creating task:", err);
      console.error("Error response:", err.response);
      
      let errorMessage = "Failed to create task";
      
      if (err.response?.status === 401) {
        errorMessage = "Session expired. Please log in again.";
        localStorage.removeItem('token');
      } else if (err.response?.status === 400) {
        console.error("Validation errors:", err.response.data);
        errorMessage = err.response.data?.detail || 
                      err.response.data?.message || 
                      (typeof err.response.data === 'string' ? err.response.data : "Invalid task data. Please check your inputs.");
      } else if (err.response?.status === 403) {
        errorMessage = "You don't have permission to create tasks.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error occurred. Please check the server logs and try again.";
        console.error("Server error details:", err.response.data);
      } else if (err.response?.status === 422) {
        console.error("Validation errors:", err.response.data);
        errorMessage = "Validation error: " + (err.response.data?.detail || "Please check your input data");
      }
      
      setErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="task-create-container">
      {/* Background Design Elements */}
      <div className="task-background-design">
      
        {/* Geometric shapes */}
        <div className="task-geometric-shape task-shape-1"></div>
        <div className="task-geometric-shape task-shape-2"></div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="message-overlay">
          <div className="success-message">
            <div className="message-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
            </div>
            <div className="message-content">
              <h3>Success!</h3>
              <p>{successMessage}</p>
            </div>
            <button 
              className="message-close"
              onClick={() => setSuccessMessage('')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="message-overlay">
          <div className="error-message-styled">
            <div className="message-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div className="message-content">
              <h3>Error!</h3>
              <p>{errorMessage}</p>
            </div>
            <button 
              className="message-close"
              onClick={() => setErrorMessage('')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="task-form-wrapper">
        <form className="task-form" onSubmit={handleSubmit}>
          <div className="task-form-header">
            <div className="task-form-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <h2 className="task-form-title">Create New Task</h2>
          </div>

          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
  <label className="form-label">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
    Client *
  </label>
  <div className="custom-select">
    <input
      type="text"
      value={client ? client : clientSearch}
      onChange={(e) => {
        setClientSearch(e.target.value);
        setClient('');
        setShowClientDropdown(true);
      }}
      onFocus={() => setShowClientDropdown(true)}
      placeholder="Search and select client..."
      className="form-input"
      required
    />
    {showClientDropdown && (
      <div className="dropdown-options">
        {filterOptions(clientOptions, clientSearch).map((option) => (
          <div
            key={option}
            className="dropdown-option"
            onClick={() => {
              setClient(option);
              setClientSearch('');
              setShowClientDropdown(false);
            }}
          >
            {option}
          </div>
        ))}
        {filterOptions(clientOptions, clientSearch).length === 0 && (
          <div className="dropdown-option disabled">No clients found</div>
        )}
      </div>
    )}
  </div>
</div>

            <div className="form-group">
  <label className="form-label">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11H5a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h4l3 3h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-3 3z"/>
    </svg>
    Task Type *
  </label>
  <div className="custom-select">
    <input
      type="text"
      value={taskType ? taskType : taskTypeSearch}
      onChange={(e) => {
        setTaskTypeSearch(e.target.value);
        setTaskType('');
        setShowTaskTypeDropdown(true);
      }}
      onFocus={() => setShowTaskTypeDropdown(true)}
      placeholder="Search and select task type..."
      className="form-input"
      required
    />
    {showTaskTypeDropdown && (
      <div className="dropdown-options">
        {filterOptions(taskTypeOptions, taskTypeSearch).map((option) => (
          <div
            key={option}
            className="dropdown-option"
            onClick={() => {
              setTaskType(option);
              setTaskTypeSearch('');
              setShowTaskTypeDropdown(false);
            }}
          >
            {option}
          </div>
        ))}
        {filterOptions(taskTypeOptions, taskTypeSearch).length === 0 && (
          <div className="dropdown-option disabled">No task types found</div>
        )}
      </div>
    )}
  </div>
</div>
          </div>

          <div className="form-group">
            <label className="form-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              Title *
            </label>
            <input
              type="text"
              placeholder="Enter task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-input"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              Description *
            </label>
            <textarea
              placeholder="Enter task description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="form-textarea"
              required
            />
          </div>

          <div className="form-row">
           <div className="form-group">
  <label className="form-label">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
    </svg>
    Priority *
  </label>
  <div className="custom-select">
    <input
      type="text"
      value={priority ? priority : prioritySearch}
      onChange={(e) => {
        setPrioritySearch(e.target.value);
        setPriority('');
        setShowPriorityDropdown(true);
      }}
      onFocus={() => setShowPriorityDropdown(true)}
      placeholder="Search and select priority..."
      className="form-input"
      required
    />
    {showPriorityDropdown && (
      <div className="dropdown-options">
        {filterOptions(priorityOptions, prioritySearch).map((option) => (
          <div
            key={option}
            className="dropdown-option"
            onClick={() => {
              setPriority(option);
              setPrioritySearch('');
              setShowPriorityDropdown(false);
            }}
          >
            {option}
          </div>
        ))}
        {filterOptions(priorityOptions, prioritySearch).length === 0 && (
          <div className="dropdown-option disabled">No priorities found</div>
        )}
      </div>
    )}
  </div>
</div>

            <div className="form-group">
  <label className="form-label">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9,11 12,14 22,4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
    Status (Default: Assigned)
  </label>
  <div className="custom-select">
    <input
      type="text"
      value={status ? status : statusSearch}
      onChange={(e) => {
        setStatusSearch(e.target.value);
        setStatus('');
        setShowStatusDropdown(true);
      }}
      onFocus={() => setShowStatusDropdown(true)}
      placeholder="Search and select status..."
      className="form-input"
    />
    {showStatusDropdown && (
      <div className="dropdown-options">
        {filterOptions(statusOptions, statusSearch).map((option) => (
          <div
            key={option}
            className="dropdown-option"
            onClick={() => {
              setStatus(option);
              setStatusSearch('');
              setShowStatusDropdown(false);
            }}
          >
            {option}
          </div>
        ))}
        {filterOptions(statusOptions, statusSearch).length === 0 && (
          <div className="dropdown-option disabled">No status options found</div>
        )}
      </div>
    )}
  </div>
</div>
          </div>

        <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
                Due Date *
              </label>
              <div className="date-input-wrapper" onClick={() => document.getElementById('due-date-input').showPicker()}>
                <input
                  id="due-date-input"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  min={currentDate}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                Due Time
              </label>
              <div className="time-input-wrapper" onClick={() => document.getElementById('due-time-input').showPicker()}>
                <input
                  id="due-time-input"
                  type="time"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  min={dueDate === currentDate ? currentTime : undefined}
                  className="form-input"
                  placeholder="Select time (optional)"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
  <label className="form-label">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
    Assignees * ({assignees.length} selected)
  </label>
  
  {/* Search input for assignees */}
  <div className="assignee-search">
    <input
      type="text"
      value={assigneeSearch}
      onChange={(e) => setAssigneeSearch(e.target.value)}
      placeholder="Search assignees..."
      className="form-input"
    />
  </div>
  
  {/* Selected Assignees Display */}
  {assignees.length > 0 && (
    <div className="selected-assignees">
      {getSelectedAssigneeNames().map((name, index) => (
        <div key={assignees[index]} className="assignee-tag">
          <span>{name}</span>
          <button
            type="button"
            onClick={() => removeAssignee(assignees[index])}
            className="remove-assignee"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      ))}
    </div>
  )}

  {/* Available Assignees */}
  <div className="assignee-dropdown">
    <div className="assignee-list">
      {loading ? (
        <div className="loading-state">
          <svg className="loading-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="2" x2="12" y2="6"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
            <line x1="2" y1="12" x2="6" y2="12"/>
            <line x1="18" y1="12" x2="22" y2="12"/>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
          </svg>
          Loading assignees...
        </div>
      ) : allUsers.length === 0 ? (
        <div className="no-assignees">
          No assignees available. Please contact your administrator.
        </div>
      ) : (
        filterUsers(allUsers, assigneeSearch).map((user) => (
          <label key={user.id} className="assignee-option">
            <input
              type="checkbox"
              checked={assignees.includes(user.id)}
              onChange={() => handleAssigneeToggle(user.id)}
              className="assignee-checkbox"
            />
            <div className="assignee-info">
              <div className="assignee-avatar">
                {(user.username || user.name || user.email || 'U').charAt(0).toUpperCase()}
              </div>
              <span className="assignee-name">
                {user.username || user.name || user.email || `User ${user.id}`}
              </span>
            </div>
            <div className="checkbox-indicator">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20,6 9,17 4,12"/>
              </svg>
            </div>
          </label>
        ))
      )}
      {filterUsers(allUsers, assigneeSearch).length === 0 && allUsers.length > 0 && (
        <div className="no-assignees">
          No assignees found matching "{assigneeSearch}"
        </div>
      )}
    </div>
  </div>
</div>

          <button type="submit" className="submit-button" disabled={loading || isSubmitting}>
            {isSubmitting ? (
              <>
                <svg className="loading-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="2" x2="12" y2="6"/>
                  <line x1="12" y1="18" x2="12" y2="22"/>
                  <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/>
                  <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/>
                  <line x1="2" y1="12" x2="6" y2="12"/>
                  <line x1="18" y1="12" x2="22" y2="12"/>
                  <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/>
                  <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
                </svg>
                Creating Task...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                Create Task
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TaskCreateForm;